import type {
  ClipboardEvent,
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  RefObject,
} from "react";
import { BorderGlow } from "@/components/ui";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import type { DictationMicControl, InputBarProps } from "./input-bar-types";
import { ImagePreviewStrip, type ImageAttachment } from "./image-preview-strip";
import { InputBarActions, InputBarMicButton } from "./input-bar-actions";
import { ComposerFilePicker } from "./composer-file-picker";

/** The input bar's text field: body text, muted placeholder, no chrome of its
 *  own, scrolling past 168px. Shared with the selection composer. */
export const INPUT_BAR_FIELD_CLASS =
  "scrollbar-none max-h-[168px] w-full min-w-0 resize-none overflow-hidden overflow-y-auto bg-transparent py-1 text-appkit-body leading-5 text-primary outline-none placeholder:text-secondary disabled:cursor-not-allowed";

type InputBarShellProps = {
  canSubmit: boolean;
  textOnly?: boolean;
  compact: boolean;
  companionDisconnected?: boolean;
  disabled: boolean;
  dynamicPlaceholder: string;
  focusedAgent: InputBarProps["focusedAgent"];
  images: ImageAttachment[];
  inputRef: RefObject<HTMLTextAreaElement | null>;
  isMultiLine: boolean;
  isStreaming: boolean;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onFixCompanion?: InputBarProps["onFixCompanion"];
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  onRemoveImage?: InputBarProps["onRemoveImage"];
  onImageFile?: InputBarProps["onImageFile"];
  conversationController?: InputBarProps["conversationController"];
  workerControls?: InputBarProps["workerControls"];
  mic?: DictationMicControl;
  nativeSurface: boolean;
  onStop?: InputBarProps["onStop"];
  onSubmit: (event: FormEvent) => void;
  onUpdateAgentEffort?: InputBarProps["onUpdateAgentEffort"];
  onUpdateAgentModel?: InputBarProps["onUpdateAgentModel"];
  providers: InputBarProps["providers"];
  value: string;
};

export function InputBarShell({
  canSubmit,
  textOnly,
  compact,
  companionDisconnected,
  disabled,
  dynamicPlaceholder,
  focusedAgent,
  images,
  inputRef,
  isMultiLine,
  isStreaming,
  onChange,
  onFixCompanion,
  onKeyDown,
  onPaste,
  onRemoveImage,
  onImageFile,
  conversationController,
  workerControls,
  mic,
  nativeSurface,
  onStop,
  onSubmit,
  onUpdateAgentEffort,
  onUpdateAgentModel,
  providers,
  value,
}: InputBarShellProps) {
  const t = useT(inputBarMessages);
  const hasImages = images.length > 0;
  const grown = isMultiLine || hasImages;
  return (
    <BorderGlow
      as="form"
      onSubmit={onSubmit}
      className={cn(
        surfaceRoleClassName("chrome"),
        "denker-input-bar-shell flex flex-col transition-all duration-200",
        !compact && "max-w-[440px]",
        // CSS fallback for the no-native-glass path. Native mode ignores this:
        // the radius comes from the Rust rail role (24px + half-height clamp).
        // Mirror it here — single-line keeps the pill (clamps to a capsule),
        // a grown bar (multi-line or attached images) pins to the rail radius
        // so corners stop scaling.
        grown && "[--input-bar-shell-radius:var(--radius-surface-rail)]",
      )}
      {...surfaceRoleAttributes("chrome", {
        ...(nativeSurface
          ? { nativeLevel: "root" as const, nativeGroup: "input-bar" }
          : {}),
      })}
      disabled={compact || disabled}
      glowRadius={24}
      glowIntensity={0.82}
      edgeSensitivity={24}
      borderWidth={2}
    >
      {hasImages && (
        <ImagePreviewStrip
          inset
          images={images}
          onRemove={(id) => onRemoveImage?.(id)}
        />
      )}
      <div
        className={cn(
          "grid gap-x-1 gap-y-1 py-1.5 pr-1.5",
          mic
            ? "grid-cols-[max-content_minmax(0,1fr)_max-content]"
            : "grid-cols-[minmax(0,1fr)_max-content]",
          // A single-line bar without a mic insets the text on its own; a
          // grown bar gives the full-width text row symmetric side insets.
          mic || grown ? "pl-1.5" : "pl-4",
          grown ? "items-end" : "items-center",
          compact ? "min-h-[44px]" : "min-h-[40px]",
        )}
      >
        {mic && (
          // A grown bar keeps the mic on the actions row, at its leading edge.
          <div
            data-composer-leading
            className={cn("flex", grown && "row-start-2")}
          >
            <InputBarMicButton mic={mic} compact={compact} />
          </div>
        )}
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={dynamicPlaceholder}
          disabled={disabled}
          rows={1}
          autoCorrect="off"
          autoCapitalize="off"
          autoComplete="off"
          spellCheck={false}
          {...{ writingsuggestions: "false" }}
          className={cn(
            INPUT_BAR_FIELD_CLASS,
            grown && "col-span-full px-2.5",
            compact && "text-appkit-subhead placeholder:text-appkit-subhead",
          )}
          aria-label={t("message")}
          data-testid="input-bar-field"
        />
        <div
          data-composer-actions
          data-testid="input-bar-actions"
          className={cn(
            "flex w-max items-center justify-end gap-0.5 justify-self-end",
            grown && "col-span-2",
          )}
        >
          <ComposerFilePicker onFile={onImageFile} disabled={disabled} />
          <InputBarActions
            value={value}
            disabled={disabled}
            isStreaming={isStreaming}
            textOnly={textOnly}
            canSubmit={canSubmit}
            compact={compact}
            focusedAgent={focusedAgent ?? null}
            conversationController={conversationController}
            workerControls={workerControls}
            providers={providers}
            onUpdateAgentModel={onUpdateAgentModel}
            onUpdateAgentEffort={onUpdateAgentEffort}
            onStop={onStop}
            companionDisconnected={companionDisconnected}
            onFixCompanion={onFixCompanion}
          />
        </div>
      </div>
    </BorderGlow>
  );
}
